import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getExamSessions } from "../../../services/ExamSessionService";
import { getResults } from "../../../services/ExamResultService";
import { getStudentProfiles } from "../../../services/StudentProfileService";
import {
  getOverviewStatistics,
  getStatisticsBySubject,
  getStatisticsBreakdown,
} from "../../../services/StatisticsService";
import { getLocale } from "../../../utils/i18nLabels";
import { useYearFilters, YearFilters } from "../Statistics/YearFilters";
import { StatCard, Spinner, Panel, Empty, scoreBadge } from "./DashboardParts";

// Tableau de bord de l'enseignant : uniquement SES étudiants (classes où il
// enseigne, via ses matières) et SES examens. Le périmètre est imposé par le
// serveur ; les filtres ne font que l'affiner.
function TeacherDashboard() {
  const { t } = useTranslation();
  const user = useSelector((state) => state.auth.auth?.user);
  const filters = useYearFilters();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [overview, setOverview] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [results, setResults] = useState([]);
  const [students, setStudents] = useState({ items: [], total: 0 });
  const [bySubject, setBySubject] = useState([]);
  const [byClass, setByClass] = useState([]);

  useEffect(() => {
    if (!filters.ready) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError("");
      const p = filters.paramsWithYear;
      try {
        const [overviewRes, subjectRes, classRes, ongoingRes, scheduledRes, resultsRes, studentsRes] = await Promise.all([
          getOverviewStatistics(p),
          getStatisticsBySubject(p),
          getStatisticsBreakdown("class", p),
          getExamSessions({ limit: 50, status: "ongoing", ...(p.class_id ? { class_id: p.class_id } : {}) }),
          getExamSessions({ limit: 50, status: "scheduled", ...(p.class_id ? { class_id: p.class_id } : {}) }),
          getResults({ limit: 8, sort_by: "corrected_at", sort_dir: "desc", ...(p.exam_id ? { exam_id: p.exam_id } : {}) }),
          getStudentProfiles({
            limit: 8,
            ...(p.class_id ? { class_id: p.class_id } : {}),
            ...(p.level_id ? { level_id: p.level_id } : {}),
            ...(p.department_id ? { department_id: p.department_id } : {}),
            ...(p.student_status ? { status: p.student_status } : {}),
          }),
        ]);
        if (cancelled) return;
        setOverview(overviewRes.data?.data || null);
        setBySubject(subjectRes.data?.data || []);
        setByClass(classRes.data?.data || []);
        setSessions(
          [...(ongoingRes.data?.data || []), ...(scheduledRes.data?.data || [])]
            .sort((a, b) => new Date(a.start_at) - new Date(b.start_at)),
        );
        setResults(resultsRes.data?.data || []);
        setStudents({ items: studentsRes.data?.data || [], total: studentsRes.data?.pagination?.total ?? 0 });
      } catch (err) {
        if (!cancelled) setLoadError(err?.response?.data?.message || t("pages.teacherDashboard.loadError"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.ready, filters.key]);

  if (!filters.ready || (loading && !overview)) return <Spinner label={t("common.crud.loading")} />;

  const r = overview?.results;
  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="bg-welcome-banner mb-3">
          <div className="card-body">
            <h1 className="text-white fs-4 mb-1">{t("pages.teacherDashboard.greeting", { name: user?.email })}</h1>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body py-3">
            <YearFilters filters={filters} small />
          </div>
        </div>

        {loadError && <div className="alert alert-warning">{loadError}</div>}

        <div className="row">
          <StatCard title={t("pages.teacherDashboard.myStudents")} value={overview?.students} icon="ti ti-school" color="primary" />
          <StatCard title={t("pages.teacherDashboard.myExams")} value={overview?.exams?.total} icon="ti ti-clipboard-text" color="info"
            subtitle={t("pages.statistics.kpiTotalSuffix", { count: overview?.exams?.published ?? 0 })} />
          <StatCard title={t("common.ui.sessionsCours")} value={overview?.sessions?.ongoing} icon="ti ti-broadcast" color="success"
            subtitle={t("pages.statistics.kpiScheduledSuffix", { count: overview?.sessions?.scheduled ?? 0 })} />
          <StatCard title={t("common.ui.tauxReussite")} value={r?.passRate != null ? `${r.passRate}%` : null} icon="ti ti-chart-pie" color="warning"
            subtitle={t("pages.statistics.kpiResultsSuffix", { count: r?.total ?? 0 })} />
        </div>
        <div className="row">
          <StatCard title={t("pages.filters.averageOutOf20")} value={r?.averageOutOf20 != null ? `${r.averageOutOf20} / 20` : null} icon="ti ti-target-arrow" color="primary" />
          <StatCard title={t("pages.teacherDashboard.passed")} value={r?.passed} icon="ti ti-circle-check" color="success" />
          <StatCard title={t("pages.teacherDashboard.failed")} value={r?.failed} icon="ti ti-circle-x" color="danger" />
          <StatCard title={t("pages.teacherDashboard.activeAttempts")} value={overview?.attempts?.in_progress} icon="ti ti-player-play" color="info" />
        </div>

        <div className="row">
          <Panel title={t("pages.teacherDashboard.upcomingSessions")} action={<Link to="/exam-sessions" className="btn btn-sm btn-outline-primary">{t("pages.dashboard.viewAll")}</Link>}>
            {sessions.length ? (
              <div className="list-group list-group-flush">
                {sessions.slice(0, 6).map((s) => (
                  <div key={s.id} className="list-group-item d-flex justify-content-between align-items-center">
                    <div>
                      <p className="mb-0 fw-medium">{t("pages.dashboard.session")} {new Date(s.start_at).toLocaleString(getLocale())}</p>
                      <small className="text-muted">{t("pages.dashboard.jusqu")} {new Date(s.end_at).toLocaleString(getLocale())}</small>
                    </div>
                    <span className={`badge badge-soft-${s.status === "ongoing" ? "primary" : "info"}`}>
                      {t(`common.examSessionStatus.${s.status}`)}
                    </span>
                  </div>
                ))}
              </div>
            ) : <Empty text={t("pages.teacherDashboard.noSessions")} />}
          </Panel>

          <Panel title={t("pages.teacherDashboard.latestResults")} action={<Link to="/results" className="btn btn-sm btn-outline-primary">{t("pages.dashboard.viewAll")}</Link>}>
            {results.length ? (
              <div className="list-group list-group-flush">
                {results.map((x) => (
                  <div key={x.id} className="list-group-item d-flex justify-content-between align-items-center">
                    <span>{x.score_out_of_20 != null ? `${Number(x.score_out_of_20).toFixed(2)} / 20` : `${x.score_percentage}%`}</span>
                    <span className={`badge badge-soft-${x.passed ? "success" : "danger"}`}>
                      {x.passed ? t("pages.teacherDashboard.passed") : t("pages.teacherDashboard.failed")}
                    </span>
                  </div>
                ))}
              </div>
            ) : <Empty text={t("pages.teacherDashboard.noResults")} />}
          </Panel>
        </div>

        <div className="row">
          <Panel title={t("pages.statistics.bySubjectTitle")}>
            <div className="table-responsive">
              <table className="table mb-0">
                <thead className="thead-light">
                  <tr>
                    <th>{t("common.fields.subject")}</th>
                    <th>{t("pages.filters.averageOutOf20")}</th>
                    <th>{t("common.ui.tauxReussite")}</th>
                  </tr>
                </thead>
                <tbody>
                  {bySubject.length ? bySubject.map((row) => (
                    <tr key={row.subjectId}>
                      <td className="fw-medium">{row.subjectName}</td>
                      <td>{row.averageOutOf20 ?? "-"}</td>
                      <td>{scoreBadge(row.passRate)}</td>
                    </tr>
                  )) : <tr><td colSpan="3" className="text-center text-muted py-3">{t("pages.statistics.noDataAvailable")}</td></tr>}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel title={t("pages.filters.byClass")}>
            <div className="table-responsive">
              <table className="table mb-0">
                <thead className="thead-light">
                  <tr>
                    <th>{t("common.fields.class")}</th>
                    <th>{t("menu.students.title")}</th>
                    <th>{t("pages.filters.averageOutOf20")}</th>
                    <th>{t("common.ui.tauxReussite")}</th>
                  </tr>
                </thead>
                <tbody>
                  {byClass.length ? byClass.map((row) => (
                    <tr key={row.key}>
                      <td className="fw-medium">{row.label}</td>
                      <td>{row.students}</td>
                      <td>{row.averageOutOf20 ?? "-"}</td>
                      <td>{scoreBadge(row.passRate)}</td>
                    </tr>
                  )) : <tr><td colSpan="4" className="text-center text-muted py-3">{t("pages.statistics.noDataAvailable")}</td></tr>}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>

        <div className="row">
          <Panel col="col-12" title={`${t("pages.teacherDashboard.myStudents")} (${students.total})`} action={<Link to="/student" className="btn btn-sm btn-outline-primary">{t("pages.dashboard.viewAll")}</Link>}>
            <div className="table-responsive">
              <table className="table mb-0">
                <thead className="thead-light">
                  <tr>
                    <th>{t("common.fields.matricule")}</th>
                    <th>{t("common.crud.statusColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {students.items.length ? students.items.map((st) => (
                    <tr key={st.id}>
                      <td><Link to={`/student/${st.id}`}>{st.matricule}</Link></td>
                      <td>{t(`pages.filters.status.${st.status}`, { defaultValue: st.status })}</td>
                    </tr>
                  )) : <tr><td colSpan="2" className="text-center text-muted py-3">{t("pages.teacherDashboard.noStudents")}</td></tr>}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

export default TeacherDashboard;